import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type {
  CancelExpectedTriggerRequest,
  LaseredCondition,
  LaseredConditionRequest,
  SetExpectedTriggerRequest,
} from "@/types";
import { apiErrorCode } from "@/utils/errors/apiErrorCode";

/**
 * Conditional lasers for one claimant on one policy (Feature #3502).
 *
 * The CRUD endpoints live under the claimant
 * (`/claimants/{claimantId}/lasered-conditions?policy_id=`), so anyone who can
 * edit claimants can use them; the list and create are scoped to the policy by
 * the query parameter, a single condition by its id. They go through the shared
 * `httpClient` like `useContractLaser`. The list is the standard
 * `{ data, pagination }` envelope. Delete is `POST .../{conditionId}/delete`.
 */
export const laseredConditionsQueryKey = (policyId?: string, claimantId?: string) => [
  "lasered-conditions",
  policyId,
  claimantId,
];

// Stable empty list so consumers' effects do not re-run on every render.
const NO_CONDITIONS: LaseredCondition[] = [];

const claimantConditionsUrl = (claimantId: string) =>
  `${getPartnerAwareApiUrl()}/claimants/${claimantId}/lasered-conditions`;

/** List / create URL: the claimant's conditions on one policy. */
export const laseredConditionsUrl = (policyId: string, claimantId: string) =>
  `${claimantConditionsUrl(claimantId)}?policy_id=${encodeURIComponent(policyId)}`;

/** One condition (GET / PUT; `/delete` appended for the delete POST). */
export const laseredConditionUrl = (claimantId: string, conditionId: string) =>
  `${claimantConditionsUrl(claimantId)}/${conditionId}`;

export const useLaseredConditions = (policyId: string | undefined, claimantId: string | undefined, enabled = true) => {
  const queryClient = useQueryClient();
  const ready = !!policyId && !!claimantId;

  const { data, isLoading, isFetching, isFetchedAfterMount, error, refetch } = useQuery({
    queryKey: laseredConditionsQueryKey(policyId, claimantId),
    queryFn: async (): Promise<LaseredCondition[]> => {
      const { json } = await httpClient(laseredConditionsUrl(policyId as string, claimantId as string));
      return ((json as { data?: LaseredCondition[] })?.data ?? []) as LaseredCondition[];
    },
    enabled: enabled && ready,
    staleTime: 0,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["lasered-conditions", policyId] });
  }, [queryClient, policyId]);

  return {
    conditions: data ?? NO_CONDITIONS,
    isLoading,
    isFetching,
    isFetchedAfterMount,
    error,
    /**
     * The API has no conditional-laser routes: a 404 with no API error envelope
     * (deployed ahead of the API). A 404 the API answers itself (not found,
     * partner access) carries a code and is a real error (!4880 thread 30530).
     */
    isUnsupported: isRouteMissing(error),
    refetch,
    invalidate,
  };
};

/**
 * Imperative writes, keyed by the claimant at call time. The dialog's create
 * flow only learns the claimant id after the straight laser is saved, so these
 * take it as an argument instead of from the hook. Only create needs the policy;
 * an existing condition is addressed by its id.
 */
export const laseredConditionApi = {
  create: async (policyId: string, claimantId: string, body: LaseredConditionRequest): Promise<LaseredCondition> => {
    const { json } = await httpClient(laseredConditionsUrl(policyId, claimantId), {
      method: "POST",
      body: JSON.stringify(body),
    });
    return json as LaseredCondition;
  },
  update: async (claimantId: string, conditionId: string, body: LaseredConditionRequest): Promise<LaseredCondition> => {
    const { json } = await httpClient(laseredConditionUrl(claimantId, conditionId), {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return json as LaseredCondition;
  },
  /** Soft delete: `POST .../{conditionId}/delete` (204). */
  remove: async (claimantId: string, conditionId: string): Promise<void> => {
    await httpClient(`${laseredConditionUrl(claimantId, conditionId)}/delete`, { method: "POST" });
  },
  /**
   * Set (or replace) the condition's expected trigger (#7361). Saved at once,
   * not with the dialog's save; returns the condition. 400 for dates outside the
   * windows, 409 for a contract-lasered or excluded claimant.
   */
  setExpectedTrigger: async (
    claimantId: string,
    conditionId: string,
    body: SetExpectedTriggerRequest
  ): Promise<LaseredCondition> => {
    const { json } = await httpClient(`${laseredConditionUrl(claimantId, conditionId)}/expected-trigger`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return json as LaseredCondition;
  },
  /** Cancel the active expected trigger (#7361); 404 when there is none. Returns the condition. */
  cancelExpectedTrigger: async (
    claimantId: string,
    conditionId: string,
    body: CancelExpectedTriggerRequest
  ): Promise<LaseredCondition> => {
    const { json } = await httpClient(`${laseredConditionUrl(claimantId, conditionId)}/expected-trigger/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return json as LaseredCondition;
  },
};

/**
 * Remove a claimant's laser on the policy. Alone it clears the straight laser
 * (or, for a claimant with nothing else, their conditional lasers or contract
 * laser). `includeConditions` also removes their live conditional lasers and
 * `includeContract` their contract laser or exclusion, so Remove can clear the
 * whole laser whatever its type (PO 2026-10-07). Sent as
 * `POST .../lasered-claimants/{id}/delete`: that needs policies:create, held by
 * every role that adds a laser, where DELETE needs policies:delete, which
 * ClaimsProcessor lacks (#3502). An API that predates the POST form answers a
 * bare 404 (no API error body) or 405; then DELETE is sent.
 *
 * `straightOnly` clears the straight laser and nothing else, and is a no-op
 * when there is none: a save that switches laser types sends it, so a retry
 * after a lost response can't remove the laser that save just wrote (!4880
 * thread 30532).
 */
export const removeLaseredClaimant = async (
  policyId: string,
  claimantId: string,
  {
    includeConditions = false,
    includeContract = false,
    straightOnly = false,
  }: { includeConditions?: boolean; includeContract?: boolean; straightOnly?: boolean } = {}
): Promise<void> => {
  const url = `${getPartnerAwareApiUrl()}/policies/${policyId}/lasered-claimants/${claimantId}`;
  const params = [
    includeConditions && "include_conditions=true",
    includeContract && "include_contract=true",
    straightOnly && "straight_only=true",
  ].filter(Boolean);
  const query = params.length ? `?${params.join("&")}` : "";
  try {
    await httpClient(`${url}/delete${query}`, { method: "POST" });
  } catch (err) {
    if (!isRouteMissing(err)) throw err;
    await httpClient(`${url}${query}`, { method: "DELETE" });
  }
};

/**
 * Put a claimant on a policy (`POST /claimants/{id}/policies/{policyId}`, as the
 * claimant page's Link Policy does). The API refuses a conditional laser for a
 * claimant who isn't on the policy, and a Conditional laser with no standing
 * deductible writes nothing else that puts them there. Already linked (409)
 * counts as done, so a retry after a lost response is safe.
 */
export const linkClaimantToPolicy = async (policyId: string, claimantId: string): Promise<void> => {
  try {
    await httpClient(`${getPartnerAwareApiUrl()}/claimants/${claimantId}/policies/${policyId}`, { method: "POST" });
  } catch (err) {
    if ((err as { status?: number } | null)?.status !== 409) throw err;
  }
};

/**
 * The route doesn't exist on this API: a 404 with no API error envelope, or a
 * 405. An API 404 ("Claimant is not lasered for this policy") carries a code
 * and is passed through, not retried as DELETE (!4880 thread 30530).
 */
export const isRouteMissing = (err: unknown): boolean => {
  const status = (err as { status?: number } | null)?.status;
  return (status === 404 && !apiErrorCode(err)) || status === 405;
};
